import { Box, Button, CircularProgress, Skeleton, Stack, Typography } from '@mui/material';
import React from 'react';
import type { WidgetProps } from '../types/index.js';
import { polygonApi } from '../../../services/polygon.js';
import { getApiEndpoint } from '../../../config/api.js';
import { selectPortfolios, usePortfolioStore } from '../../portfolios/portfolioStore.js';
import { LogoImage } from '../../../components/LogoImage.js';

export const ProfileHeaderWidget: React.FC<WidgetProps> = ({ widget, onUpdateOutputs }) => {
  const profile = widget.inputs.find(i => i.id === 'profile')?.value as any | undefined;
  const profileId = (widget.config as any)?.profileId as string | undefined;
  const portfolios = usePortfolioStore(selectPortfolios);
  const addSymbols = usePortfolioStore((state) => state.addSymbols);
  const removeSymbol = usePortfolioStore((state) => state.removeSymbol);
  const defaultPortfolio = React.useMemo(
    () => portfolios.find((p) => p.isDefault) ?? portfolios[0] ?? null,
    [portfolios]
  );
  const [portfolioUpdating, setPortfolioUpdating] = React.useState(false);
  const [portfolioError, setPortfolioError] = React.useState<string | null>(null);
  const lastTickerRef = React.useRef<string | undefined>(undefined);
  const [brandingLogoUrl, setBrandingLogoUrl] = React.useState<string | undefined>(undefined);
  const [brandingIconUrl, setBrandingIconUrl] = React.useState<string | undefined>(undefined);
  const portfolioTicker = profile?.ticker ? profile.ticker.toUpperCase() : null;

  const matchingPortfolioInstrument = React.useMemo(() => {
    if (!defaultPortfolio || !portfolioTicker) return null;
    const targetKey = profile?.instrumentId;
    return (
      defaultPortfolio.instruments.find((instrument) => {
        if (targetKey && instrument.key === targetKey) {
          return true;
        }
        const symbol = instrument.symbol?.toUpperCase();
        const displaySymbol = instrument.displaySymbol?.toUpperCase();
        return symbol === portfolioTicker || displaySymbol === portfolioTicker;
      }) ?? null
    );
  }, [defaultPortfolio, portfolioTicker, profile?.instrumentId]);

  const inPortfolio = Boolean(matchingPortfolioInstrument);

  const handlePortfolioToggle = React.useCallback(async () => {
    if (!portfolioTicker) {
      setPortfolioError('Ticker unavailable for this profile.');
      return;
    }
    if (!defaultPortfolio) {
      setPortfolioError('Create a portfolio to start tracking tickers.');
      return;
    }

    setPortfolioUpdating(true);
    try {
      if (matchingPortfolioInstrument) {
        const identifier =
          matchingPortfolioInstrument.key ||
          matchingPortfolioInstrument.symbol ||
          matchingPortfolioInstrument.displaySymbol ||
          portfolioTicker;
        const mode: 'symbol' | 'key' = matchingPortfolioInstrument.key ? 'key' : 'symbol';

        const attemptRemoval = async (options?: { expectedVersion?: number }) => {
          await removeSymbol(defaultPortfolio.id, identifier, { mode, ...options });
        };

        try {
          await attemptRemoval({ expectedVersion: defaultPortfolio.version });
        } catch (error: any) {
          const status = error?.response?.status ?? error?.status;
          if (status === 409) {
            await attemptRemoval();
          } else if (mode === 'key') {
            const fallbackSymbol =
              matchingPortfolioInstrument.symbol ||
              matchingPortfolioInstrument.displaySymbol ||
              portfolioTicker;
            if (fallbackSymbol) {
              await removeSymbol(defaultPortfolio.id, fallbackSymbol, { mode: 'symbol' });
            } else {
              throw error;
            }
          } else {
            throw error;
          }
        }
      } else {
        await addSymbols(defaultPortfolio.id, {
          symbols: [portfolioTicker],
          expectedVersion: defaultPortfolio.version,
        });
      }
      setPortfolioError(null);
    } catch (error: any) {
      console.warn('[ProfileHeaderWidget] Portfolio update failed', error);
      const message =
        error?.response?.data?.message ||
        (error instanceof Error ? error.message : 'Failed to update portfolio');
      setPortfolioError(message);
    } finally {
      setPortfolioUpdating(false);
    }
  }, [addSymbols, defaultPortfolio, matchingPortfolioInstrument, portfolioTicker, removeSymbol]);
  React.useEffect(() => {
    const t = profile?.ticker as string | undefined;
    if (t && lastTickerRef.current !== t) {
      lastTickerRef.current = t;
      onUpdateOutputs({ ticker: t });
    }
  }, [profile?.ticker]);

  React.useEffect(() => {
    let cancelled = false;
    const t = profile?.ticker as string | undefined;
    if (!t) return;
    (async () => {
      try {
        const info = await polygonApi.getCompanyInfo(t);
        if (cancelled) return;
        setBrandingLogoUrl(info.brandingLogoUrl);
        setBrandingIconUrl(info.brandingIconUrl);
      } catch (e) {
        // Ignore; UI will fall back to profile.logoUrl
      }
    })();
    return () => { cancelled = true; };
  }, [profile?.ticker]);

  if (!profile) {
    return (
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Skeleton variant="circular" width={48} height={48} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width={200} />
            <Skeleton width={300} />
          </Box>
          <Skeleton width={220} height={28} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={2} alignItems="center">
          <LogoImage
            src={
              profileId
                ? `${getApiEndpoint()}/profile/${encodeURIComponent(profileId)}/logo`
                : (profile.logoUrl || brandingLogoUrl || brandingIconUrl)
            }
            alt={profile.ticker}
            size={48}
            paddingPx={2}
            fallbackText={(profile.ticker || profile.name || '?')}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>
              {profile.name} ({profile.ticker})
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {[profile.sector, profile.industry].filter(Boolean).join(' • ')}
            </Typography>
          </Box>
          <Button
            size="small"
            variant={inPortfolio ? 'outlined' : 'contained'}
            color={inPortfolio ? 'secondary' : 'primary'}
            disabled={portfolioUpdating || !defaultPortfolio || !portfolioTicker}
            startIcon={portfolioUpdating ? <CircularProgress size={14} color="inherit" /> : undefined}
            onClick={handlePortfolioToggle}
          >
            {inPortfolio ? 'Remove from Portfolio' : 'Add to Portfolio'}
          </Button>
        </Stack>
        {portfolioError && (
          <Typography variant="caption" color="error" sx={{ textAlign: 'right' }}>
            {portfolioError}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
